import { create } from "zustand";

export interface FileWithStatus extends File {
  status: "pending" | "uploading" | "completed" | "failed" | "duplicate";
  _id?: string;
  blob_id?: string;
  duplicate?: boolean;
  error?: string;
  tempId?: string;
  progress?: number;
  uploadStartedAt?: number;
  totalBytes?: number;
  locked?: boolean;
  driveId?: string;
  originalFile?: File;
  existingBlobId?: string;
  existingPath?: string[];
  existingName?: string;
}

export interface DuplicateUploadInfo {
  uploadedName: string;
  existingBlobId: string;
  existingName: string;
  existingPath: string[];
}

interface FileTrayStoreType {
  everUploaded: boolean;
  files: FileWithStatus[];
  addFile: (file: FileWithStatus) => void;
  removeFile: (file: FileWithStatus) => void;
  updateFile: (file: FileWithStatus) => void;
  emptyTray: () => void;
  duplicateUploads: DuplicateUploadInfo[];
  addDuplicateUpload: (info: DuplicateUploadInfo) => void;
  clearDuplicateUploads: () => void;
  // Global upload dialog state management
  globalUploadDialog: {
    isOpen: boolean;
    prependFilePath: string[];
    metaData: {
      orgId?: string;
      workspaceId?: string;
      useFilesystem?: boolean;
      driveId?: string;
    };
    driveSelection: boolean;
  };
  openGlobalUploadDialog: (config: {
    prependFilePath: string[];
    metaData: {
      orgId?: string;
      workspaceId?: string;
      useFilesystem?: boolean;
      driveId?: string;
    };
    driveSelection?: boolean;
  }) => void;
  closeGlobalUploadDialog: () => void;
}

const useFileTrayStore = create<FileTrayStoreType>()((set) => ({
  everUploaded: false,
  files: [],
  addFile: (file) =>
    set((state) => ({ files: [...state.files, file], everUploaded: true })),
  removeFile: (file) =>
    set((state) => ({ files: state.files.filter((f) => f._id !== file._id) })),
  updateFile: (file) =>
    set((state) => ({
      files: state.files.map((f) =>
        (f._id && f._id === file._id) || (f.tempId && f.tempId === file.tempId)
          ? file
          : f
      ),
    })),
  emptyTray: () => set({ files: [] }),
  duplicateUploads: [],
  addDuplicateUpload: (info) =>
    set((state) => ({
      duplicateUploads: [...state.duplicateUploads, info],
    })),
  clearDuplicateUploads: () => set({ duplicateUploads: [] }),
  // Global upload dialog state management
  globalUploadDialog: {
    isOpen: false,
    prependFilePath: [],
    metaData: {},
    driveSelection: false,
  },
  openGlobalUploadDialog: (config) =>
    set({
      globalUploadDialog: {
        isOpen: true,
        prependFilePath: config.prependFilePath,
        metaData: config.metaData,
        driveSelection: config.driveSelection || false,
      },
    }),
  closeGlobalUploadDialog: () =>
    set((state) => ({
      globalUploadDialog: {
        ...state.globalUploadDialog,
        isOpen: false,
      },
    })),
}));

export default useFileTrayStore;
